import { useCallback, useEffect, useRef, useState } from 'react';

type ExportFormat = 'csv' | 'json';

type ExportTarget =
  | 'reports:sales'
  | 'reports:items'
  | 'reports:incidents'
  | 'reports:closeout'
  | 'orders'
  | 'inventory';

export type ExportDeliveryDestination = 'accounting' | 'inventory' | 'warehouse';

export interface ExportDeliveryInput {
  mode: 'download' | 'webhook';
  destination?: ExportDeliveryDestination;
}

interface ExportDeliverySummary {
  mode: 'download' | 'webhook';
  destination: ExportDeliveryDestination | null;
  status: 'idle' | 'pending' | 'delivered' | 'failed';
  deliveredAt: number | null;
  responseStatus: number | null;
  errorMessage: string | null;
}

type ExportStatus = 'idle' | 'preparing' | 'processing' | 'downloading' | 'success' | 'error';

interface ExportJobSummary {
  id: string;
  target: ExportTarget;
  format: ExportFormat;
  status: 'processing' | 'completed' | 'failed';
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
  expiresAt: number;
  filename: string | null;
  errorMessage: string | null;
  delivery: ExportDeliverySummary | null;
  downloadUrl: string | null;
}

interface ExportJobState {
  status: ExportStatus;
  job: ExportJobSummary | null;
  error: Error | null;
}

interface UseExportJobOptions<F extends Record<string, unknown>> {
  target: ExportTarget;
  getFilters?: () => F;
  onSuccess?: (job: ExportJobSummary) => void;
  onError?: (error: Error) => void;
  pollIntervalMs?: number;
}

interface UseExportJobResult<F extends Record<string, unknown>> {
  status: ExportStatus;
  job: ExportJobSummary | null;
  error: Error | null;
  isRunning: boolean;
  startExport: (
    format: ExportFormat,
    overrides?: Partial<F>,
    delivery?: ExportDeliveryInput,
  ) => Promise<void>;
  reset: () => void;
}

const DEFAULT_POLL_INTERVAL = 1000;

const fetchJson = async (input: RequestInfo, init?: RequestInit) => {
  const response = await fetch(input, init);
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const body = (await response.json()) as { message?: string; code?: string };
      if (body?.message) {
        message = body.message;
      } else if (body?.code) {
        message = body.code;
      }
    } catch {
      // Ignore parse errors; fallback to default message
    }
    throw new Error(message);
  }
  return (await response.json()) as Record<string, unknown>;
};

const downloadJobFile = async (job: ExportJobSummary, format: ExportFormat) => {
  if (!job.downloadUrl) {
    throw new Error('Export completed without a download URL');
  }

  const response = await fetch(job.downloadUrl, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  const filename =
    job.filename ?? `${job.target.replace(/[:]/g, '-')}-${job.id}.${format}`;

  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
};

export function useExportJob<F extends Record<string, unknown> = Record<string, unknown>>(
  options: UseExportJobOptions<F>,
): UseExportJobResult<F> {
  const [state, setState] = useState<ExportJobState>({
    status: 'idle',
    job: null,
    error: null,
  });

  const isMountedRef = useRef(true);
  const activeJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const pollJob = useCallback(
    async (jobId: string): Promise<ExportJobSummary> => {
      const payload = await fetchJson(`/api/reports/exports/${jobId}`, {
        credentials: 'include',
      });
      const job = payload?.job as ExportJobSummary | undefined;
      if (!job) {
        throw new Error('Export job status unavailable');
      }
      return job;
    },
    [],
  );

  const startExport = useCallback(
    async (format: ExportFormat, overrides?: Partial<F>, delivery?: ExportDeliveryInput) => {
      if (state.status === 'preparing' || state.status === 'processing') {
        return;
      }

      const handleCompletedJob = async (job: ExportJobSummary) => {
        if (job.status !== 'completed') {
          throw new Error(job.errorMessage ?? 'Export failed to complete');
        }

        if (job.delivery?.mode === 'webhook') {
          if (!isMountedRef.current) {
            return;
          }
          setState({ status: 'success', job, error: null });
          options.onSuccess?.(job);
          if (!isMountedRef.current) {
            return;
          }
          setState({ status: 'idle', job: null, error: null });
          return;
        }

        if (!job.downloadUrl) {
          throw new Error('Export completed without a download URL');
        }

        if (!isMountedRef.current) {
          return;
        }
        setState({ status: 'downloading', job, error: null });

        await downloadJobFile(job, format);

        if (!isMountedRef.current) {
          return;
        }
        setState({ status: 'success', job, error: null });
        options.onSuccess?.(job);
        if (!isMountedRef.current) {
          return;
        }
        setState({ status: 'idle', job: null, error: null });
      };

      setState({
        status: 'preparing',
        job: null,
        error: null,
      });

      try {
        const baseFilters = options.getFilters ? options.getFilters() : ({} as F);
        const filters = {
          ...baseFilters,
          ...(overrides ?? {}),
        };

        const requestBody: Record<string, unknown> = {
          target: options.target,
          format,
          filters,
        };

        if (delivery) {
          requestBody.delivery = delivery;
        }

        const payload = await fetchJson('/api/reports/exports', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        const initialJob = payload?.job as ExportJobSummary | undefined;
        if (!initialJob) {
          throw new Error('Export request did not return a job');
        }

        activeJobIdRef.current = initialJob.id;

        if (initialJob.status === 'completed') {
          await handleCompletedJob(initialJob);
          return;
        }

        setState({
          status: 'processing',
          job: initialJob,
          error: null,
        });

        const pollInterval = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL;

        let latestJob = initialJob;

        while (isMountedRef.current) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
          latestJob = await pollJob(initialJob.id);

          if (!isMountedRef.current) {
            return;
          }

          if (latestJob.status === 'completed' || latestJob.status === 'failed') {
            break;
          }

          setState((previous) =>
            previous.job && previous.job.id === latestJob.id
              ? {
                  status: previous.status,
                  job: latestJob,
                  error: previous.error,
                }
              : previous,
          );
        }

        if (!isMountedRef.current) {
          return;
        }

        await handleCompletedJob(latestJob);
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        const err = error instanceof Error ? error : new Error('Export failed');
        setState({
          status: 'error',
          job: null,
          error: err,
        });
        options.onError?.(err);
      } finally {
        activeJobIdRef.current = null;
      }
    },
    [state.status, options, pollJob],
  );

  const reset = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }
    activeJobIdRef.current = null;
    setState({
      status: 'idle',
      job: null,
      error: null,
    });
  }, []);

  const isRunning =
    state.status === 'preparing' ||
    state.status === 'processing' ||
    state.status === 'downloading';

  return {
    status: state.status,
    job: state.job,
    error: state.error,
    isRunning,
    startExport,
    reset,
  };
}
